import { computed, Injectable, signal, inject } from '@angular/core';
import {
  AppShortcut,
  PerformanceComparison,
  SkillsRadar,
  StatCard,
} from '../models';
import { LoggerService } from '../../../shared/services/logger.service';
import { ChamadosStore } from '../../copom/chamados/data-access/chamados.store';
import { AlertasStore } from '../../sentinela/alertas/data-access/alertas.store';
import { RapStore } from '../../rap/data-access/rap.store';
import { TciStore } from '../../tci/data-access/tci.store';
import { FeatureFlagService } from '../../../core/feature-flags';
import { AuthService } from '../../../core/auth/services/auth.service';

/** Roles que habilitam a homologação de RAP pelo Oficial de Dia (ADR-0019/ADR-0027). */
const ROLES_HOMOLOGACAO_RAP = ['app.nexus.oficial-dia', 'app.nexus.comandante', 'app.nexus.inteligencia'];

@Injectable({
  providedIn: 'root',
})
export class DashboardStore {
  private readonly logger = inject(LoggerService);
  private readonly chamadosStore = inject(ChamadosStore);
  private readonly alertasStore = inject(AlertasStore);
  private readonly rapStore = inject(RapStore);
  private readonly tciStore = inject(TciStore);
  private readonly flagService = inject(FeatureFlagService);
  private readonly authService = inject(AuthService);

  // ─── Estado base ─────────────────────────────────────────────────────────────
  private readonly _isLoading = signal(false);
  readonly isLoading = this._isLoading.asReadonly();

  private readonly _erro = signal<string | null>(null);
  readonly erro = this._erro.asReadonly();

  private readonly _asOf = signal<string | null>(null);
  readonly asOf = this._asOf.asReadonly();

  private readonly _performance = signal<PerformanceComparison>({
    categories: [],
    series: { policial: [], unidade: [], pmdf: [] },
  });
  readonly performance = this._performance.asReadonly();

  private readonly _skills = signal<SkillsRadar>({
    dimensions: [],
    values: [],
  });
  readonly skills = this._skills.asReadonly();

  // ─── Computed ─────────────────────────────────────────────────────────────────
  readonly temErro = computed(() => this._erro() !== null);

  readonly asOfFormatted = computed(() => {
    const asOf = this._asOf();
    if (!asOf) return null;
    return new Date(asOf).toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
    });
  });

  readonly statCards = computed<StatCard[]>(() => {
    const chamados = this.chamadosStore.totalChamados();
    const sentinela = this.alertasStore.paginacao().encontrados;
    const rapsFinalizados = this.rapStore.contadoresPorStatus().finalizados;
    const tci = this.tciStore.totalItens();
    const flags = this.flagService.flags();

    const cards: StatCard[] = [];

    if (flags.copom) {
      cards.push({ label: 'Chamados', value: chamados, icon: 'phone_in_talk', color: '#ef4444', route: '/copom' });
    }
    if (flags.sentinela) {
      cards.push({ label: 'DF 360', value: sentinela, svgIcon: 'sentinela', colorClass: 'text-pmdf-primary', route: '/sentinela' });
    }
    if (flags.tci) {
      cards.push({ label: 'TCIs', value: tci, icon: 'description', color: '#3b82f6', route: '/tci' });
    }
    if (flags.rap && flags.dashboardAtalhosCompletos) {
      cards.push({ label: 'RAPs', value: rapsFinalizados, icon: 'assignment_turned_in', color: '#22c55e', route: '/rap' });
    }

    return cards;
  });

  readonly appShortcuts = computed<AppShortcut[]>(() => {
    const raps = this.rapStore.contadoresPorStatus().total;
    const flags = this.flagService.flags();

    const shortcuts: AppShortcut[] = [];

    if (flags.rap) {
      shortcuts.push({ id: 'rap', label: 'RAP', svgIcon: 'rap', bgClass: 'bg-pmdf-success-soft', iconClass: 'text-pmdf-success', route: '/rap', badge: raps });
    }
    if (flags.eleicoesPosto) {
      shortcuts.push({ id: 'eleicoes', label: 'Eleições', svgIcon: 'eleicoes', bgClass: 'bg-pmdf-base-primary', iconClass: 'text-pmdf-primary', route: '/eleicoes/posto' });
    }
    if (flags.rap && this.authService.hasAnyRole(ROLES_HOMOLOGACAO_RAP)) {
      shortcuts.push({ id: 'rap-homologacao', label: 'Homologação', svgIcon: 'homologacao', bgClass: 'bg-pmdf-base-primary', iconClass: 'text-pmdf-primary', route: '/rap/validacao' });
    }
    if (flags.consultaPessoa) {
      shortcuts.push({ id: 'consulta-pessoa', label: 'Pessoa', svgIcon: 'consulta-pessoa', bgClass: 'bg-pmdf-base-primary', iconClass: 'text-pmdf-primary', route: '/consultas/pessoa' });
    }
    if (flags.consultaVeiculo) {
      shortcuts.push({ id: 'consulta-veiculo', label: 'Veículo', svgIcon: 'consulta-veiculo', bgClass: 'bg-pmdf-info-soft', iconClass: 'text-pmdf-info', route: '/consultas/veiculo' });
    }
    if (flags.consultaBicicleta) {
      shortcuts.push({ id: 'consulta-bicicleta', label: 'Bicicleta', icon: 'pedal_bike', bgClass: 'bg-pmdf-success-soft', iconClass: 'text-pmdf-success', route: '/consultas/bicicleta' });
    }
    if (flags.consultaCelular) {
      shortcuts.push({ id: 'consulta-celular', label: 'Celular', icon: 'smartphone', bgClass: 'bg-pmdf-warning-soft', iconClass: 'text-pmdf-warning', route: '/consultas/celular' });
    }
    if (flags.sentinela) {
      shortcuts.push({ id: 'sentinela', label: 'DF 360', svgIcon: 'sentinela', bgClass: 'bg-pmdf-info-soft', iconClass: 'text-pmdf-primary', route: '/sentinela' });
    }
    if (flags.tci) {
      shortcuts.push({ id: 'tci', label: 'TCIs', svgIcon: 'tci', bgClass: 'bg-pmdf-warning-soft', iconClass: 'text-pmdf-muted-70', route: '/tci' });
    }
    if (flags.copom && flags.dashboardAtalhosCompletos) {
      shortcuts.push({ id: 'copom', label: 'COPOM', svgIcon: 'copom', bgClass: 'bg-pmdf-danger-soft', iconClass: 'text-pmdf-danger', route: '/copom' });
    }
    if (flags.provid) {
      shortcuts.push({ id: 'provid', label: 'PROVID', svgIcon: 'provid', bgClass: 'bg-pmdf-danger-soft', iconClass: 'text-pmdf-danger', route: '/provid/workspace' });
    }
    if (flags.ordemServico) {
      shortcuts.push({ id: 'os', label: 'OS', svgIcon: 'ordem-servico', bgClass: 'bg-pmdf-base-primary', iconClass: 'text-pmdf-primary', route: '/ordem-servico' });
    }
    if (flags.viaturaAbastecimento) {
      shortcuts.push({ id: 'viatura', label: 'Viatura', svgIcon: 'viatura', bgClass: 'bg-pmdf-bg-muted', iconClass: 'text-pmdf-muted-70', route: '/viatura/abastecimento' });
    }
    if (flags.viaturaVistoria) {
      shortcuts.push({ id: 'vistoria', label: 'Vistoria', svgIcon: 'viatura', bgClass: 'bg-pmdf-bg-muted', iconClass: 'text-pmdf-primary', route: '/viatura/vistoria' });
    }
    if (flags.abordagemRapida) {
      shortcuts.push({ id: 'abordagem', label: 'Abordagem', svgIcon: 'abordagem', bgClass: 'bg-pmdf-bg-muted', iconClass: 'text-pmdf-secondary', route: '/rap/abordagem' });
    }
    if (flags.rapFaunaResgate) {
      shortcuts.push({ id: 'registro-campo', label: 'Campo', svgIcon: 'rap', bgClass: 'bg-pmdf-success-soft', iconClass: 'text-pmdf-success', route: '/rap/registro-campo' });
    }

    return shortcuts;
  });

  // ─── Carregamento ────────────────────────────────────────────────────────────

  async loadDashboard(): Promise<void> {
    this._isLoading.set(true);
    this._erro.set(null);
    await this._carregarFeatureStores();
    this._isLoading.set(false);
  }

  /** Atualização silenciosa (polling). Não exibe spinner nem limpa o erro existente. */
  async carregarSilencioso(): Promise<void> {
    if (this._isLoading()) return;
    await this._carregarFeatureStores();
  }

  private async _carregarFeatureStores(): Promise<void> {
    try {
      this.tciStore.carregarTcis();

      await Promise.allSettled([
        this.chamadosStore.carregarChamados(),
        this.alertasStore.carregarAlertas(),
        this.rapStore.carregarRaps(),
      ]);

      this._asOf.set(new Date().toISOString());
      this._erro.set(null);
    } catch (error) {
      this.logger.error('[DashboardStore] Erro ao carregar dados do dashboard', error);
      this._erro.set('Não foi possível atualizar os dados.');
    }
  }
}
